// src/context/persona/assist/PersonalizeControl.jsx — "Adatta con l'AI" under one persona row (v197,
// /tenant-admin/personas only). Optional instructions + documents → a background personalize job
// (`onStart(body)`). The note says what changes (context and Pain & Gain) and what stays (role, language).
// ⛔ NOT a form element (decision 9): only the button starts the job; the textarea keeps Enter for new lines.
// v198 — a STEP of the persona modal ("Indicazioni"): no panel chrome and no title, the button in <ModalFooter>
// (no Annulla: the modal closes itself). No base / advanced split — both inputs are already optional. A start
// refusal (`startError`) shows on top through StartNotice; `onDirtyChange` reports anything typed or ticked.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DocumentPicker from '../../DocumentPicker.jsx';
import { ModalFooter } from '../../../components/ui/WizardModal.jsx';
import {
  choiceHint, fieldLabel, fieldName, footerRow, primaryBtn, sectionTextarea,
} from '../personaStyles.js';
import { INSTRUCTION_MAX, buildPersonalizeBody } from './assistModel.js';
import { StartNotice } from './JobCard.jsx';

export default function PersonalizeControl({
  tenantId, personaId, busy = false, onStart, startError = null, onDismissStartError, onDirtyChange,
}) {
  const { t } = useTranslation('context');
  const [instruction, setInstruction] = useState('');
  const [selected, setSelected] = useState([]);
  const [pickerBusy, setPickerBusy] = useState(false);
  const disabled = busy || pickerBusy;
  const dirty = instruction.trim() !== '' || selected.length > 0;
  useEffect(() => { if (onDirtyChange) onDirtyChange(dirty); }, [dirty, onDirtyChange]);

  function start() {
    if (disabled) return;
    onStart(buildPersonalizeBody(personaId, instruction, selected));
  }

  return (
    <div data-testid="persona-personalize-control">
      <StartNotice startError={startError} onDismiss={onDismissStartError} />

      {/* The hint says what to write and the placeholder shows an example. The text has the HIGHEST precedence
          for the AI (above documents, brand guidelines and Knowledge Base) and also steers the KB search, so
          concrete facts about this kind of customer are what make the persona precise. The hint sits outside
          the <label> (tied by aria-describedby) so it does not become part of the field's name. */}
      <div style={{ marginBottom: '14px' }}>
        <label htmlFor="persona-personalize-instruction" style={fieldLabel}>
          <span style={fieldName}>{t('personas.assist.instruction')}</span>
        </label>
        <p id="persona-personalize-instruction-hint" style={{ ...choiceHint, marginTop: 0, marginBottom: '8px' }}>
          {t('personas.assist.instructionHint')}
        </p>
        <textarea
          id="persona-personalize-instruction" aria-describedby="persona-personalize-instruction-hint"
          rows={5} value={instruction} maxLength={INSTRUCTION_MAX} disabled={busy}
          placeholder={t('personas.assist.instructionPlaceholder')}
          onChange={(ev) => setInstruction(ev.target.value)} style={sectionTextarea(busy)}
        />
      </div>

      {/* The documents section: a title and a hint, like "Indicazioni" above (the shared picker draws no heading
          of its own — each caller names it). Documents rank right after the guidance for the AI. */}
      <div style={{ marginBottom: '8px' }}>
        <h3 style={{ ...fieldName, margin: '0 0 6px' }}>
          {t('personas.assist.referenceDocsTitle')}
        </h3>
        <p style={{ ...choiceHint, marginTop: 0, marginBottom: '10px' }}>{t('personas.assist.referenceDocsHint')}</p>
      </div>
      <DocumentPicker
        tenantId={tenantId} label={t('personas.assist.docsLabel')} selected={selected} onChange={setSelected}
        disabled={busy} onBusyChange={setPickerBusy}
      />

      <ModalFooter>
        <div style={footerRow}>
          <span style={{ flex: 1 }} />
          <button type="button" disabled={disabled} onClick={start} style={primaryBtn(disabled)}>
            {t('personas.assist.personalizeSubmit')}
          </button>
        </div>
      </ModalFooter>
    </div>
  );
}
